/**
 * HubPage — gedeeld tooloverzicht van een sectie (Productie, Planning, Mijn werk, AI,
 * Operations, Lab). Leest SECONDARY_NAV_SECTIONS.
 *
 * Hub-herziening 25 sep 2026 (Joris: "hier mag ook een rustiger / logischer scherm"):
 * geen kaarten meer maar een stille lijst — per tool één regel met een klein grijs
 * lijn-icoon, de naam en één regel omschrijving. Groepen zijn werkstappen met een
 * stille kop (geen gekleurd icoon, geen teller). Geen "AI"-label: AI is geen
 * eigenschap waar je op kiest. "beta" blijft een klein grijs woord en staat er alleen
 * als de BETA-schakelaar aan staat (anders is het item helemaal niet zichtbaar).
 *
 * Omschrijving: `item.hub` (navigation.js) wint van de `itemMeta` van de hub-wrapper.
 */
import React from 'react';
import { Box, Typography } from '@mui/material';
import { Link } from 'react-router-dom';
import { useUserRole } from '../contexts/UserRoleContext';
import { useVisibilityFilters, isItemVisible } from '../contexts/VisibilityFiltersContext';
import { SECONDARY_NAV_SECTIONS } from '../config/navigation';
import PageHeader from './PageHeader';

const T = {
  surfaceLow: '#f1f4f5',
  text: '#2d3335', outline: '#767c7e', outlineVariant: '#adb3b5',
  fontBody: '"Inter", system-ui, sans-serif',
};

const HULPMIDDELEN = 'Hulpmiddelen';

const Icon = ({ name, size = 18, sx = {} }) => (
  <span
    aria-hidden="true"
    className="material-symbols-outlined"
    style={{ fontSize: size, fontVariationSettings: "'FILL' 0, 'wght' 300", ...sx }}
  >
    {name}
  </span>
);

// Een item met `externalUrl` (bv. Formaat omzetten in Flow) opent in een nieuw tabblad.
const linkProps = (item) => (item.externalUrl
  ? { component: 'a', href: item.externalUrl, target: '_blank', rel: 'noopener noreferrer' }
  : { component: Link, to: item.path });

const ExternIcoon = ({ item }) => (item.externalUrl
  ? <Box component="span" title="Opent in een nieuw tabblad" sx={{ display: 'inline-flex', ml: 0.5, flexShrink: 0, color: '#adb3b5' }}><Icon name="open_in_new" size={13} /></Box>
  : null);

/**
 * Pure opbouw van de hubgroepen: filtert op featurerecht en BETA/PLANNED-schakelaars,
 * groepeert op `group` (volgorde: `section.hubGroepen`, anders eerste voorkomen) en zet
 * Hulpmiddelen (tier "utility") altijd achteraan. Elk item krijgt `meta` { icon, desc }.
 */
export function bouwHubGroepen(section, { permissions = {}, visibility = { showBeta: true, showPlanned: false }, itemMeta = {} } = {}) {
  if (!section) return [];
  const groepen = [];
  const perLabel = {};
  const hulp = { label: HULPMIDDELEN, items: [] };

  section.items.forEach((item) => {
    if (item.path === section.hubPath) return;
    if (item.feature && !permissions?.[item.feature]?.view) return;
    if (!isItemVisible(item, visibility)) return;
    const meta = { icon: 'link', desc: '', ...(itemMeta[item.path] || {}), ...(item.hub || {}) };
    const regel = { ...item, meta };
    if (item.tier === 'utility' || item.group === HULPMIDDELEN) {
      hulp.items.push(regel);
      return;
    }
    const label = item.group || 'Overig';
    if (!perLabel[label]) {
      perLabel[label] = { label, items: [] };
      groepen.push(perLabel[label]);
    }
    perLabel[label].items.push(regel);
  });

  const volgorde = section.hubGroepen || [];
  const rang = (g) => {
    const i = volgorde.indexOf(g.label);
    return i === -1 ? volgorde.length : i;
  };
  const gesorteerd = groepen
    .map((g, i) => ({ g, i }))
    .sort((a, b) => rang(a.g) - rang(b.g) || a.i - b.i)
    .map(({ g }) => g);

  return hulp.items.length ? [...gesorteerd, hulp] : gesorteerd;
}

function StilWoord({ children, title }) {
  return (
    <Box component="span" title={title} sx={{
      fontSize: '0.68rem', fontWeight: 500, fontFamily: T.fontBody,
      color: T.outlineVariant, lineHeight: 1, ml: 0.75, flexShrink: 0,
    }}>
      {children}
    </Box>
  );
}

function ToolRegel({ item }) {
  const { meta } = item;
  return (
    <Box
      {...linkProps(item)}
      sx={{
        display: 'flex', alignItems: 'flex-start', gap: 1.5,
        px: 1.25, py: 1, borderRadius: '8px',
        textDecoration: 'none', color: 'inherit',
        opacity: item.planned ? 0.55 : 1,
        transition: 'background-color 0.12s',
        '&:hover': { bgcolor: T.surfaceLow, opacity: 1 },
        '&:focus-visible': { outline: `2px solid ${T.outlineVariant}`, outlineOffset: 1 },
      }}
    >
      <Icon name={meta.icon} sx={{ color: T.outlineVariant, marginTop: 1, flexShrink: 0 }} />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', minWidth: 0 }}>
          <Typography component="span" noWrap sx={{ fontFamily: T.fontBody, fontSize: '0.86rem', fontWeight: 600, color: T.text }}>
            {item.label}
          </Typography>
          <ExternIcoon item={item} />
          {item.beta && <StilWoord title="Nog in ontwikkeling">beta</StilWoord>}
          {item.planned && <StilWoord title="Nog niet gebouwd">gepland</StilWoord>}
        </Box>
        {meta.desc && (
          <Typography noWrap title={meta.desc} sx={{ fontFamily: T.fontBody, fontSize: '0.76rem', color: T.outline, lineHeight: 1.45 }}>
            {meta.desc}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

// headerIcon/headerAccent blijven als props bestaan (wrappers geven ze mee), maar de kop
// is bewust neutraal: klein grijs icoon, geen gekleurd vlak.
export default function HubPage({ sectionKey, title, subtitle, headerIcon, itemMeta = {} }) {
  const { permissions } = useUserRole();
  const visibility = useVisibilityFilters();
  const section = SECONDARY_NAV_SECTIONS[sectionKey];
  if (!section) return null;

  const groepen = bouwHubGroepen(section, { permissions, visibility, itemMeta });

  return (
    <Box sx={{ width: '100%', px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 3, md: 5 }, pb: 10 }}>
      <Box sx={{ mb: { xs: 2, md: 3.5 } }}>
        <PageHeader
          dense
          icon={headerIcon || 'dashboard'}
          title={title || section.label}
          subtitle={subtitle}
          accent={{ color: T.outline, bg: T.surfaceLow }}
        />
      </Box>

      {groepen.length === 0 ? (
        <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.88rem', color: T.outline, py: 6 }}>
          Geen tools beschikbaar voor jouw rol.
        </Typography>
      ) : (
        // Groepen stromen als krantenkolommen: 1 op mobiel, 2 op tablet/laptop, 3 op breed
        <Box sx={{ columnCount: { xs: 1, md: 2, xl: 3 }, columnGap: { md: 5, xl: 6 }, mx: -1.25 }}>
          {groepen.map((groep) => (
            <Box
              key={groep.label}
              component="section"
              aria-label={groep.label}
              sx={{ breakInside: 'avoid', display: 'inline-block', width: '100%', mb: 3.5 }}
            >
              <Typography component="h2" sx={{
                fontFamily: T.fontBody, fontSize: '0.72rem', fontWeight: 600,
                color: T.outline, px: 1.25, mb: 0.5,
              }}>
                {groep.label}
              </Typography>
              {groep.items.map((item) => <ToolRegel key={item.path} item={item} />)}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}
